import React, { useContext, useMemo } from "react";
import type { LucideIcon } from "lucide-react";

import { IntlContext } from "context";
import { getDeviceIcon } from "constants/devices";
import { sortCodesByDisplayOrder } from "helpers/reference/sortCodesByDisplayOrder";
import { useReferences, type getReferenceReturn } from "hooks/reference";
import type { CatalogService } from "types/service";
import { cn } from "lib/utils";

interface Props {
    serviceData?: CatalogService["data"];
    compact?: boolean;
    className?: string;
}

type OptionGroupConfig = {
    dataKey: keyof NonNullable<CatalogService["data"]>;
    refType: keyof getReferenceReturn;
    labelKey: string;
};

const OPTION_GROUPS: OptionGroupConfig[] = [
    { dataKey: "device_type", refType: "device", labelKey: "our_services.label.device_type" },
    { dataKey: "scholar_subjects", refType: "scholar_subject", labelKey: "service.label.subject" },
    { dataKey: "academic_level", refType: "scholar_level", labelKey: "our_services.label.academic_level" },
    { dataKey: "foreign_languages", refType: "foreign_languages", labelKey: "service.label.foreign_languages" },
    { dataKey: "activities", refType: "activity", labelKey: "service.label.activity" },
];

type ResolvedOption = {
    code: string;
    label: string;
    Icon?: LucideIcon;
};

type ResolvedGroup = {
    key: string;
    title: string;
    options: ResolvedOption[];
};

function readCodes(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

export function useProviderServiceOptionGroups(serviceData?: CatalogService["data"]): ResolvedGroup[] {
    const intl = useContext(IntlContext);
    const reference = useReferences();

    return useMemo<ResolvedGroup[]>(() => {
        if (!serviceData) {
            return [];
        }

        return OPTION_GROUPS.flatMap(({ dataKey, refType, labelKey }) => {
            const codes = readCodes(serviceData[dataKey]);
            if (codes.length === 0) {
                return [];
            }

            return [
                {
                    key: dataKey,
                    title: intl.formatMessage(labelKey),
                    options: sortCodesByDisplayOrder(codes, reference.all(refType)).map((code) => ({
                        code,
                        label: reference.get(refType, code)?.value ?? code,
                        Icon: dataKey === "device_type" ? getDeviceIcon(code) : undefined,
                    })),
                },
            ];
        });
    }, [intl, reference, serviceData]);
}

export const ProviderServiceOptions: React.FC<Props> = ({ serviceData, compact = false, className }) => {
    const groups = useProviderServiceOptionGroups(serviceData);

    if (groups.length === 0) {
        return null;
    }

    return (
        <div className={cn(compact ? "space-y-2" : "space-y-3", className)}>
            {groups.map((group) => (
                <div key={group.key}>
                    <p
                        className={
                            compact
                                ? "mb-1 text-[11px] font-semibold text-muted-foreground"
                                : "mb-1.5 text-xs font-semibold text-muted-foreground"
                        }
                    >
                        {group.title}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {group.options.map(({ code, label, Icon }) => (
                            <span
                                key={code}
                                className={
                                    compact
                                        ? "inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                                        : "inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary"
                                }
                            >
                                {Icon !== undefined && (
                                    <Icon
                                        className={compact ? "size-3 shrink-0" : "size-3.5 shrink-0"}
                                        aria-hidden="true"
                                    />
                                )}
                                {label}
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};
